<script setup>
import { inject, ref, watch } from "vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogDescription,
} from "@headlessui/vue";
import Text from "@/v2/primitives/Text/Text.vue";
import Button from "@/v2/primitives/Button/Button.vue";
import Icon from "@/v2/primitives/Icon/Icon.vue";

import * as appIdle from "@/components/Global/providedData/appIdle.ts";

const { isIdle } = inject(appIdle.key, appIdle.defaultApi);

defineProps({
  title: {
    type: String,
    default: null,
  },
  hasBackdrop: {
    type: Boolean,
    default: true,
  },
  hasHeader: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String,
    default: "sm",
    validator(val) {
      return ["sm", "md", "lg", "fullscreen", null].includes(val);
    },
  },
  icon: {
    type: String,
    default: null,
  },
  isFullBleed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);

const isOpen = ref(false);
const panelRef = ref(null);

function setIsOpen(value) {
  isOpen.value = value;
  if (!value) {
    emit("close");
  }
}

watch(
  () => isIdle.value,
  () => {
    if (isIdle.value) {
      setIsOpen(false);
    }
  }
);

defineExpose({
  isOpen,
  open: () => setIsOpen(true),
  close: () => setIsOpen(false),
});
</script>

<template>
  <Dialog
    :open="isOpen"
    class="dialog"
    :initial-focus="panelRef"
    aria-label="Dialog"
    @close="setIsOpen"
  >
    <div v-if="hasBackdrop" class="dialog__backdrop"></div>
    <div class="dialog__container">
      <DialogPanel
        ref="panelRef"
        class="dialog__panel"
        :class="{
          'dialog__panel--no-header': !hasHeader,
          'dialog__panel--sm': size === 'sm',
          'dialog__panel--md': size === 'md',
          'dialog__panel--lg': size === 'lg',
          'dialog__panel--fullscreen': size === 'fullscreen',
          'dialog__panel--auto': size === null,
          'dialog__panel--full-bleed': isFullBleed,
        }"
      >
        <div class="dialog__panel--content" data-testid="dialog-content">
          <DialogTitle v-if="hasHeader" class="dialog__header" as="div">
            <div class="dialog__header--content">
              <Icon v-if="icon && !isFullBleed" :name="icon" size="title3" />
              <Text variant="title3" class="dialog__header--text" as="h3">{{
                !isFullBleed ? title : ""
              }}</Text>
            </div>
            <Button
              variant="primary"
              elevation="raised"
              tooltip="Close"
              tooltip-position="inline-start"
              data-testid="dialog-close-button"
              :data-dd="`Dialog Close Button: ${title}`"
              @click="() => setIsOpen(false)"
            >
              <template #left-icon>
                <Icon name="xmark" />
              </template>
            </Button>
          </DialogTitle>

          <DialogDescription class="dialog__body" as="div">
            <slot />
          </DialogDescription>

          <div class="dialog__buttons">
            <slot name="buttons"></slot>
          </div>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<style lang="scss" scoped>
@import "@/v2/styles/main";

.dialog {
  &__backdrop {
    position: fixed;
    height: 100%;
    width: 100%;
    inset: 0;
    z-index: var(--z-index-dialog-backdrop);
    background: var(--color-dialog-overlay);
  }

  &__container {
    position: fixed;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    inset: 0;
    z-index: var(--z-index-dialog-container);
    max-height: 100vh;
    max-width: 100vw;
    padding: var(--spacing-base-md);

    @include min-width-sm {
      padding: var(--spacing-md);
    }

    @include min-width-md {
      padding: var(--spacing-lg);
    }
  }

  &__panel {
    box-shadow: var(--shadow-overlaid-borderless);
    position: relative;
    background-color: var(--record-list-surface);
    border-radius: var(--border-radius-medium);
  }

  &__panel--auto {
    height: 100%;
    width: 100%;
  }

  &__panel--sm {
    width: grid-column(4);
    max-height: 95vh;
  }

  &__panel--md {
    width: grid-column(6);
    max-height: 95vh;
  }

  &__panel--lg {
    width: grid-column(8);
    max-height: 95vh;
  }

  &__panel--fullscreen {
    width: 100%;
    height: 100%;
    flex-grow: 1;
  }

  &__panel--content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-base);
  }

  &__header {
    border-top-left-radius: var(--border-radius-medium);
    border-top-right-radius: var(--border-radius-medium);
    background-color: var(--record-list-name-surface);
    padding: var(--spacing-base-sm) var(--spacing-base-sm)
      var(--spacing-base-sm) var(--spacing-base);
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: var(--shadow-overlaid-borderless);
    border-bottom: 4px solid var(--record-list-divider);
    z-index: 1;
  }

  &__header--content {
    display: flex;
    justify-content: flex-start;
    color: var(--colors-primary-neutral);
    gap: var(--spacing-sm);
  }

  &__header--text {
    font-weight: bold;
    line-height: 1;
  }

  &__body {
    height: 100%;
    width: 100%;
    flex-grow: 1;
    padding: 0 var(--spacing-base);
    color: var(--colors-primary-neutral);
    overflow-y: hidden;
  }

  &__buttons {
    display: flex;
    gap: var(--spacing-base);
    justify-content: center;
    border-bottom-left-radius: var(--border-radius-medium);
    border-bottom-right-radius: var(--border-radius-medium);
    width: 100%;
  }

  &__buttons:has(> *) {
    padding-bottom: var(--spacing-base);
  }
}

.dialog__panel--no-header .dialog__body {
  padding-top: var(--spacing-base);
  border-top-left-radius: var(--border-radius-medium);
  border-top-right-radius: var(--border-radius-medium);
}

.dialog__panel--full-bleed {
  .dialog__panel--content {
    position: relative;
  }

  .dialog__header {
    height: fit-content;
    inset: 0;
    position: absolute;
    background: none;
    border: none;
    box-shadow: none;
  }

  .dialog__buttons {
    height: fit-content;
    bottom: 0;
    position: absolute;
  }

  .dialog__body {
    border-radius: var(--border-radius-medium);
    position: absolute;
    padding: 0;
    inset: 0;
  }
}
</style>
